import React from 'react';
import { JSONTree } from 'react-json-tree';
import { Button } from '~/components/Button';
import style from './index.module.scss';

const jsonTreeLightTheme = {
  base00: 'transparent',
  base01: 'transparent',
  base02: '#e0e0e0',
  base03: '#9e9e9e',
  base04: '#616161',
  base05: '#424242',
  base06: '#212121',
  base07: '#000000',
  base08: '#e53935',
  base09: '#fb8c00',
  base0A: '#fdd835',
  base0B: '#43a047',
  base0C: '#00acc1',
  base0D: '#1e88e5',
  base0E: '#8e24aa',
  base0F: '#6d4c41',
};

const shouldExpandNodeInitially = (keyPath: Array<string | number>, data: unknown): boolean => {
  if (!data || typeof data !== 'object') {
    return false;
  }

  try {
    if (Array.isArray(data)) {
      return data.length === 1;
    }

    return Object.keys(data as Record<string, unknown>).length === 1;
  } catch {
    return false;
  }
};

interface JsonTreeViewerProps {
  data: unknown;
  showCopy?: boolean;
}

export const JsonTreeViewer: React.FC<JsonTreeViewerProps> = ({ data, showCopy = false }) => {
  let json = '';

  try {
    json = JSON.stringify(data, null, 2);
  } catch {
    json = '';
  }

  const handleCopy = () => {
    if (!json) {
      return;
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(json).catch(() => {
        const textarea = document.createElement('textarea');
        textarea.value = json;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      });
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = json;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  };

  return (
    <div className={style.tree}>
      {showCopy && (
        <Button
          small
          responsive
          className={style.copyButton}
          onClick={handleCopy}
          leftIcon={<i className="far fa-copy" />}
        />
      )}
      <JSONTree
        data={data}
        theme={jsonTreeLightTheme}
        shouldExpandNodeInitially={shouldExpandNodeInitially}
        hideRoot={false}
      />
    </div>
  );
};
